import { UserCheck, Users, Home, UtensilsCrossed } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

// Types matching the plan's data structure
export type RSVP = {
  id: string;
  eventId: string;
  email: string | null; // JUB-85: organizer-created RSVPs may have no email
  phone?: string;
  familyName?: string;
  tableName?: string | null;
  status: "confirmed" | "pending" | "declined";
  submittedAt?: string;
  notes?: string | null; // JUB-58: guest's free-text message / dietary note (per RSVP group)
  guests: Guest[];
};

export type Guest = {
  id: string;
  rsvpId: string;
  name: string;
  type: "adult" | "kid";
  menuPreference?: "standard" | "vegetarian" | "vegan";
  accommodationNeeded?: boolean;
  tableName?: string | null;
  ceremonyAttendance?: {
    civil?: boolean;
    religious?: boolean;
    baptism?: boolean;
    party?: boolean;
  };
};

interface GuestSummaryCardsProps {
  rsvps: RSVP[];
}

export function GuestSummaryCards({ rsvps }: GuestSummaryCardsProps) {
  const { t } = useTranslation();

  // Calculate metrics
  const confirmedGuests = rsvps
    .filter((rsvp) => rsvp.status === "confirmed")
    .flatMap((rsvp) => rsvp.guests);
  const confirmedAdults = confirmedGuests.filter((g) => g.type === "adult").length;
  const confirmedKids = confirmedGuests.filter((g) => g.type === "kid").length;

  const pendingRSVPs = rsvps.filter((rsvp) => rsvp.status === "pending");
  // JUB-68: people (not families) who said they cannot come — same unit as
  // the confirmed figure next to it (JUB-78 label-units rule).
  const declinedCount = rsvps
    .filter((rsvp) => rsvp.status === "declined")
    .flatMap((rsvp) => rsvp.guests).length;

  // JUB-68: families that declined need no room or menu — keep the venue
  // counters to people who might actually show up.
  const attendingGuests = rsvps
    .filter((rsvp) => rsvp.status !== "declined")
    .flatMap((rsvp) => rsvp.guests);

  const needAccommodation = attendingGuests.filter((g) => g.accommodationNeeded).length;

  const menuSelections = attendingGuests.filter((g) => g.menuPreference);
  const standardMenu = menuSelections.filter((g) => g.menuPreference === "standard").length;
  const vegetarianMenu = menuSelections.filter((g) => g.menuPreference === "vegetarian").length;
  const veganMenu = menuSelections.filter((g) => g.menuPreference === "vegan").length;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Confirmed Guests Card - matches reference design */}
      <div className="rounded-lg bg-card text-card-foreground shadow-sm shadow-card border-0">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100">
              <UserCheck className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{confirmedGuests.length}</p>
              <p className="text-xs text-muted-foreground">
                {t("guests.confirmedGuests")}
              </p>
            </div>
          </div>
          {(confirmedAdults > 0 || confirmedKids > 0) && (
            <div className="mt-3 pt-3 border-t flex gap-4 text-xs text-muted-foreground">
              {confirmedAdults > 0 && (
                <span>
                  <strong className="text-foreground">{confirmedAdults}</strong> {t("guests.adults")}
                </span>
              )}
              {confirmedKids > 0 && (
                <span>
                  <strong className="text-foreground">{confirmedKids}</strong> {t("guests.kids")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pending RSVPs Card - matches reference design */}
      <div className="rounded-lg bg-card text-card-foreground shadow-sm shadow-card border-0">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <Users className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{pendingRSVPs.length}</p>
              <p className="text-xs text-muted-foreground">
                {t("guests.pendingRsvps")}
              </p>
            </div>
          </div>
          {declinedCount > 0 && (
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">{declinedCount}</strong> {t("guests.declined")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Need Accommodation Card - matches reference design */}
      <div className="rounded-lg bg-card text-card-foreground shadow-sm shadow-card border-0">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-100">
              <Home className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{needAccommodation}</p>
              <p className="text-xs text-muted-foreground">
                {t("guests.needAccommodation") || t("rsvp.accommodation")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Selections Card - matches reference design */}
      <div className="rounded-lg bg-card text-card-foreground shadow-sm shadow-card border-0">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-purple-100">
              <UtensilsCrossed className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{menuSelections.length}</p>
              <p className="text-xs text-muted-foreground">
                {t("guests.menuSelections") || t("rsvp.menuPreference")}
              </p>
            </div>
          </div>
          {(standardMenu > 0 || vegetarianMenu > 0 || veganMenu > 0) && (
            <div className="mt-3 pt-3 border-t flex gap-3 text-xs text-muted-foreground">
              {standardMenu > 0 && (
                <span>
                  <strong className="text-foreground">{standardMenu}</strong> {t("guests.standard") || t("rsvp.menu.standard")}
                </span>
              )}
              {vegetarianMenu > 0 && (
                <span>
                  <strong className="text-foreground">{vegetarianMenu}</strong> {t("guests.vegetarian") || t("rsvp.menu.vegetarian")}
                </span>
              )}
              {veganMenu > 0 && (
                <span>
                  <strong className="text-foreground">{veganMenu}</strong> {t("guests.vegan") || t("rsvp.menu.vegan")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

